<script setup lang="ts">
import VarsityInlineConfirm from "@/components/VarsityInlineConfirm.vue"
import VarsityInlineConfirmButton from "@/components/VarsityInlineConfirmButton.vue"
import { createInlineConfirm } from "@/composables/createInlineConfirm"
import type { ChampionshipRegistrationSelectTeamTask } from "@/model/championship/championship-registration-select-team-task"
import type { TasksProps } from "@/pages/Tasks/Tasks/tasks-props"
import { useHttpClient, useHttpGet } from "@/vf"
import { computed, ref, toRefs } from "vue"
import { type ChampionshipRegistrationApproveData } from "./championship-registration"
import ChampionshipRegistrationApprovalStateList, {
    type ApprovalResult,
} from "./internal/ChampionshipRegistrationApprovalStateList.vue"
import ChampionshipRegistrationResetButton from "./internal/ChampionshipRegistrationResetButton.vue"
import ChampionshipRegistrationTeamList from "./internal/ChampionshipRegistrationTeamList.vue"
import { useRegistrationPolicy } from "./useRegistrationPolicy"

const props = defineProps<TasksProps<ChampionshipRegistrationSelectTeamTask>>()

const emit = defineEmits<{
    (e: "updated"): void
}>()

const { task } = toRefs(props)
const { data } = await useHttpGet<ChampionshipRegistrationApproveData>(
    `/championship/registration/${task.value.organizationId}/approve/${task.value.id}`,
)

const http = useHttpClient()

const approvalResult = ref<ApprovalResult>({})
const policy = useRegistrationPolicy(
    computed(() => data.value?.permissions),
    computed(() => props.viewMode),
)

const inlineConfirm = createInlineConfirm({
    async action() {
        await submit(approvalResult.value)
    },
})

/*─────────────────────────────────────┐
│  submit                              │
└─────────────────────────────────────*/
async function submit(result: ApprovalResult) {
    await http.post(`/championship/registration/${data.value!.registrationId}/approve?viewMode=${props.viewMode}`, {
        teams: result,
    })
    emit("updated")
}
</script>
<template>
    <div class="p-5">
        <div v-if="viewMode === 'customer'">
            <div class="alert alert-info mb-5 mt-3">
                {{ $t("@tasks:tasks.championship_registration.approve.approval_pending") }}
            </div>

            <ChampionshipRegistrationTeamList
                :registration-id="data!.registrationId"
                :teams="data!.teams"
                :view-mode="props.viewMode"
                :permissions="data?.permissions"
            ></ChampionshipRegistrationTeamList>

            <div class="mt-5">
                <ChampionshipRegistrationResetButton
                    :registrationId="data!.registrationId"
                    :view-mode="props.viewMode"
                    v-if="policy.ResetRegistration"
                    @resetted="emit('updated')"
                />
            </div>
        </div>

        <div v-else>
            <ChampionshipRegistrationApprovalStateList
                v-model="approvalResult"
                v-bind="{ data: data!, teams: data!.teams, viewMode: props.viewMode, permissions: data?.permissions }"
            >
                <template #bottom="{ allTeamsDecided }">
                    <div class="d-flex justify-content-end mt-5 text-right">
                        <div>
                            <VarsityInlineConfirmButton
                                :controller="inlineConfirm"
                                :disabled="!allTeamsDecided || !policy.SaveApprovalStates"
                            >
                                Überprüfung finalisieren
                            </VarsityInlineConfirmButton>
                            <VarsityInlineConfirm :controller="inlineConfirm">
                                <template #confirmation>
                                    <p>
                                        Bist du dir sicher, dass du die Überprüfung der Teams finalisieren möchtest?
                                        Diese Entscheidung kann nicht rückgängig gemacht werden.
                                    </p>
                                </template>
                                <template #success>Die Überprüfung der Teams wurde erfolgreich finalisiert.</template>
                            </VarsityInlineConfirm>
                        </div>
                    </div>
                </template>
            </ChampionshipRegistrationApprovalStateList>
        </div>
    </div>
</template>
